import type { Row, SqlStatement, SqlValue, StorageAdapter } from "./adapter.js";

/**
 * Adapter backed by @tauri-apps/plugin-sql. Loaded only on Tauri builds;
 * importing this file in the browser will fail at runtime when load() is called.
 */
export class TauriSqliteAdapter implements StorageAdapter {
  private db: unknown = null;
  constructor(private readonly url: string = "sqlite:reminders.db") {}

  async open(): Promise<void> {
    if (this.db) return;
    // Indirection prevents Vite from trying to resolve the package in the
    // browser build, where this code path is never reached.
    const spec = "@tauri-apps/plugin-sql";
    const mod = (await import(/* @vite-ignore */ spec)) as { default: { load(u: string): Promise<unknown> } };
    this.db = await mod.default.load(this.url);
  }

  async close(): Promise<void> {
    if (!this.db) return;
    const d = this.db as { close(): Promise<void> };
    await d.close();
    this.db = null;
  }

  async execute(sql: string, params: ReadonlyArray<SqlValue> = []) {
    const d = await this.ensure();
    const res = (await d.execute(sql, params as SqlValue[])) as { rowsAffected: number };
    return { rowsAffected: res.rowsAffected };
  }

  async executeBatch(statements: ReadonlyArray<SqlStatement>) {
    if (statements.length === 0) return { rowsAffected: 0 };
    const d = await this.ensure();
    let rowsAffected = 0;
    await d.execute("BEGIN IMMEDIATE", []);
    try {
      for (const s of statements) {
        const res = (await d.execute(s.sql, (s.params ?? []) as SqlValue[])) as { rowsAffected?: number };
        rowsAffected += res.rowsAffected ?? 0;
      }
      await d.execute("COMMIT", []);
    } catch (e) {
      try { await d.execute("ROLLBACK", []); } catch { /* ignore rollback failure */ }
      throw e;
    }
    return { rowsAffected };
  }

  async select<T = Row>(sql: string, params: ReadonlyArray<SqlValue> = []): Promise<T[]> {
    const d = await this.ensure();
    return (await d.select(sql, params as SqlValue[])) as T[];
  }

  private async ensure() {
    if (!this.db) await this.open();
    return this.db as { execute(s: string, p: SqlValue[]): Promise<unknown>; select(s: string, p: SqlValue[]): Promise<unknown> };
  }
}
